import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase } from '../config/api';
import { useAuth } from '../context/AuthContext'; // We need this to get the token
import '../styles/admin.css';

// --- Reusable Message Component ---
const Message = ({ text, type }) => {
  if (!text) return null;
  return <div className={`alert alert-${type} mt-3`}>{text}</div>;
};

// --- Form to Create New Admins ---
const CreateUserForm = ({ onUserCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { token } = useAuth(); // Get the auth token

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Name, Email, and Password are required.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // We must send the Authorization token to prove we are superadmin
      await axios.post(
        `${getApiBase()}/api/auth/register`,
        { ...form, confirmPassword: form.password }, // Add confirmPassword to pass validation
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setSuccess(`User "${form.name}" created successfully!`);
      setForm({ name: '', email: '', password: '', role: 'admin' }); // Reset form
      onUserCreated(); // Refresh the list of users
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Create New User</h5>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input type="text" className="form-control" id="name" name="name"
                value={form.name} onChange={handleChange} placeholder="User's Full Name" />
            </div>
            <div className="col-md-4">
              <label htmlFor="email" className="form-label">Email</label>
              <input type="email" className="form-control" id="email" name="email"
                value={form.email} onChange={handleChange} placeholder="user@example.com" />
            </div>
            <div className="col-md-4">
              <label htmlFor="password" className="form-label">Password</label>
              <input type="password" className="form-control" id="password" name="password"
                value={form.password} onChange={handleChange} placeholder="Create a password" />
            </div>
          </div>
          <Message text={error} type="danger" />
          <Message text={success} type="success" />
          <button type="submit" className="btn btn-admin-primary mt-3" disabled={loading}>
            {loading ? 'Creating...' : '+ Create User'}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- Main Page Component ---
const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, token } = useAuth(); // Get current user's role and token

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBase()}/api/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // We need a DELETE user function, let's add it to server.js and here.
  // For now, this is just the UI.

  // Only a superadmin should see this page, but we also hide the form
  // if they somehow get here.
  const isSuperAdmin = user && user.role === 'superadmin';

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h1 className="display-4 fw-bold mb-3 admin-dashboard-title">Manage Users</h1>
      
      {error && <Message text={error} type="danger" />}
      
      {isSuperAdmin ? (
        <CreateUserForm onUserCreated={fetchData} />
      ) : (
        <Message text="You do not have permission to create new users." type="warning" />
      )}
      
      <div className="admin-card">
        <div className="card-body">
          <h5 className="mb-3">All Admin Users</h5>
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="fw-bold">{u.name}</td>
                    <td className="text-muted">{u.email}</td>
                    <td>
                      {u.role === 'superadmin' ? (
                        <span className="badge bg-success">Super Admin</span>
                      ) : (
                        <span className="badge bg-secondary">Admin</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${u.isActive ? 'bg-success-light text-success' : 'bg-danger-light text-danger'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {/* You would add Edit/Delete buttons here */}
                      <button className="btn btn-sm btn-outline-secondary" disabled>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;